export type CyzorAppId =
  | 'control'
  | 'crm'
  | 'erp'
  | 'projects'
  | 'people'
  | 'docs'
  | 'bi'
  | 'automate'
  | 'forms'
  | 'sign'
  | 'neural'
  | 'finance'
  | 'sales'
  | 'customer'
  | 'growth'
  | 'operations'
  | 'procurement'
  | 'supply'
  | 'production'
  | 'quality'
  | 'enterprise'
  | 'technology'
  | 'cybersecurity'
  | 'data'
  | 'knowledge'
  | 'product'
  | 'innovation'
  | 'ai'
  | 'automation'
  | 'facilities'
  | 'assets'
  | 'communication'
  | 'health-safety'
  | 'international'
  | 'franchise'
  | 'ecommerce'
  | 'ecosystem'
  | 'research';

export type AppStatus = 'available' | 'coming_soon' | 'disabled';

export type AppCategory =
  | 'control'
  | 'people'
  | 'finance'
  | 'sales'
  | 'customer'
  | 'growth'
  | 'operations'
  | 'projects'
  | 'procurement'
  | 'supply'
  | 'production'
  | 'quality'
  | 'enterprise'
  | 'technology'
  | 'cybersecurity'
  | 'data'
  | 'knowledge'
  | 'product'
  | 'innovation'
  | 'ai'
  | 'automation'
  | 'facilities'
  | 'assets'
  | 'communication'
  | 'health-safety'
  | 'international'
  | 'franchise'
  | 'ecommerce'
  | 'ecosystem'
  | 'research';

export const APP_CATEGORY_LABELS: Record<AppCategory, string> = {
  control: 'Control',
  people: 'People',
  finance: 'Finance',
  sales: 'Sales',
  customer: 'Customer',
  growth: 'Growth',
  operations: 'Operations',
  projects: 'Projects',
  procurement: 'Procurement',
  supply: 'Supply',
  production: 'Production',
  quality: 'Quality',
  enterprise: 'Enterprise',
  technology: 'Technology',
  cybersecurity: 'Cybersecurity',
  data: 'Data',
  knowledge: 'Knowledge',
  product: 'Product',
  innovation: 'Innovation',
  ai: 'AI',
  automation: 'Automation',
  facilities: 'Facilities',
  assets: 'Assets',
  communication: 'Communication',
  'health-safety': 'Health & Safety',
  international: 'International',
  franchise: 'Franchise',
  ecommerce: 'E-commerce',
  ecosystem: 'Ecosystem',
  research: 'Research',
};

export interface NavigationItem {
  id: string;
  label: string;
  path: string;
  icon?: string;
  badge?: string | number;
  children?: NavigationItem[];
  permissions?: string[];
}

export interface CyzorAppManifest {
  id: CyzorAppId;
  slug: string;
  name: string;
  description: string;
  category: AppCategory;
  icon?: string;
  basePath: string;
  dashboardPath: string;
  route: string;
  status: AppStatus;
  version: string;
  permissions?: readonly string[];
  dependencies?: readonly string[];
  capabilities?: readonly string[];
  navigation: NavigationItem[];
  enabled: boolean;
}

export const APP_REGISTRY: Record<CyzorAppId, CyzorAppManifest> = {
  control: {
    id: 'control',
    slug: 'control',
    name: 'Control',
    description: 'Plataforma central e administração do ecossistema CYZOR',
    category: 'control',
    icon: 'LayoutDashboard',
    basePath: '/control',
    dashboardPath: '/control',
    route: '/control',
    status: 'available',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/control' },
      { id: 'pulse', label: 'Pulse', path: '/control/pulse' },
      { id: 'agenda', label: 'Agenda', path: '/control/calendar', icon: 'Calendar' },
      { id: 'settings', label: 'Configurações', path: '/control/settings' },
      { id: 'profile', label: 'Perfil', path: '/control/profile' },
    ],
    enabled: true,
  },
  crm: {
    id: 'crm',
    slug: 'crm',
    name: 'CRM',
    description: 'Gestão de relacionamento com clientes',
    category: 'sales',
    icon: 'Users',
    basePath: '/crm',
    dashboardPath: '/crm',
    route: '/crm',
    status: 'available',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/crm' },
      { id: 'customers', label: 'Clientes', path: '/crm/customers' },
    ],
    enabled: true,
  },
  erp: {
    id: 'erp',
    slug: 'erp',
    name: 'ERP',
    description: 'Gestão empresarial: produtos, vendas e financeiro',
    category: 'finance',
    icon: 'Package',
    basePath: '/erp',
    dashboardPath: '/erp',
    route: '/erp',
    status: 'available',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/erp' },
      { id: 'products', label: 'Produtos', path: '/erp/products' },
      { id: 'finance', label: 'Financeiro', path: '/erp/finance' },
    ],
    enabled: true,
  },
  projects: {
    id: 'projects',
    slug: 'projects',
    name: 'Projects',
    description: 'Execução de projetos, tarefas e ideias',
    category: 'projects',
    icon: 'GitBranch',
    basePath: '/projects',
    dashboardPath: '/projects',
    route: '/projects',
    status: 'available',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/projects' },
      { id: 'projects', label: 'Projetos', path: '/projects/projects' },
      { id: 'ideas', label: 'Ideias', path: '/projects/ideas' },
    ],
    enabled: true,
  },
  people: {
    id: 'people',
    slug: 'people',
    name: 'People',
    description: 'Gestão de pessoas, equipes e organização',
    category: 'people',
    icon: 'User',
    basePath: '/people',
    dashboardPath: '/people',
    route: '/people',
    status: 'available',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/people' },
      { id: 'people', label: 'Pessoas', path: '/people/people' },
    ],
    enabled: true,
  },
  docs: {
    id: 'docs',
    slug: 'docs',
    name: 'Docs',
    description: 'Documentos e conhecimento',
    category: 'knowledge',
    icon: 'FileText',
    basePath: '/docs',
    dashboardPath: '/docs',
    route: '/docs',
    status: 'available',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/docs' },
      { id: 'documents', label: 'Documentos', path: '/docs/documents' },
    ],
    enabled: true,
  },
  bi: {
    id: 'bi',
    slug: 'bi',
    name: 'BI',
    description: 'Inteligência de negócio e analytics',
    category: 'data',
    icon: 'BarChart3',
    basePath: '/bi',
    dashboardPath: '/bi',
    route: '/bi',
    status: 'available',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/bi' },
    ],
    enabled: true,
  },
  automate: {
    id: 'automate',
    slug: 'automate',
    name: 'Automate',
    description: 'Workflows e automações',
    category: 'automation',
    icon: 'Workflow',
    basePath: '/automate',
    dashboardPath: '/automate',
    route: '/automate',
    status: 'available',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/automate' },
      { id: 'flow-builder', label: 'Flow Builder', path: '/automate/flow-builder' },
    ],
    enabled: true,
  },
  forms: {
    id: 'forms',
    slug: 'forms',
    name: 'Formulários',
    description: 'Formulários, respostas e templates',
    category: 'automation',
    icon: 'FileQuestion',
    basePath: '/forms',
    dashboardPath: '/forms',
    route: '/forms',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [],
    enabled: true,
  },
  sign: {
    id: 'sign',
    slug: 'sign',
    name: 'Sign',
    description: 'Assinatura digital e fluxos de assinatura',
    category: 'enterprise',
    icon: 'PenLine',
    basePath: '/sign',
    dashboardPath: '/sign',
    route: '/sign',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [],
    enabled: true,
  },
  neural: {
    id: 'neural',
    slug: 'neural',
    name: 'Neural',
    description: 'IA e inteligência transversal',
    category: 'ai',
    icon: 'BotMessageSquare',
    basePath: '/neural',
    dashboardPath: '/neural',
    route: '/neural',
    status: 'available',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/neural' },
      { id: 'ia', label: 'Conversas', path: '/neural' },
    ],
    enabled: true,
  },

 finance: {
    id: 'finance',
    slug: 'finance',
    name: 'Finance',
    description: 'Gestão financeira, contas a pagar e a receber, tesouraria',
    category: 'finance',
    icon: 'PiggyBank',
    basePath: '/finance',
    dashboardPath: '/finance',
    route: '/finance',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/finance' },
    ],
    enabled: true,
  },
  sales: {
    id: 'sales',
    slug: 'sales',
    name: 'Sales',
    description: 'Pipeline de vendas, oportunidades e operações de receita',
    category: 'sales',
    icon: 'BarChart3',
    basePath: '/sales',
    dashboardPath: '/sales',
    route: '/sales',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/sales' },
    ],
    enabled: true,
  },
  customer: {
    id: 'customer',
    slug: 'customer',
    name: 'Customer',
    description: 'Central de atendimento, suporte e sucesso do cliente',
    category: 'customer',
    icon: 'Headset',
    basePath: '/customer',
    dashboardPath: '/customer',
    route: '/customer',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/customer' },
    ],
    enabled: true,
  },
  growth: {
    id: 'growth',
    slug: 'growth',
    name: 'Growth',
    description: 'Marketing, campanhas e iniciativas de crescimento',
    category: 'growth',
    icon: 'TrendingUp',
    basePath: '/growth',
    dashboardPath: '/growth',
    route: '/growth',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/growth' },
    ],
    enabled: true,
  },
  operations: {
    id: 'operations',
    slug: 'operations',
    name: 'Operations',
    description: 'Processos operacionais, ordens de serviço e planejamento',
    category: 'operations',
    icon: 'Settings',
    basePath: '/operations',
    dashboardPath: '/operations',
    route: '/operations',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/operations' },
    ],
    enabled: true,
  },
  procurement: {
    id: 'procurement',
    slug: 'procurement',
    name: 'Procurement',
    description: 'Compras, fornecedores e gestão de contratos',
    category: 'procurement',
    icon: 'ShoppingCart',
    basePath: '/procurement',
    dashboardPath: '/procurement',
    route: '/procurement',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/procurement' },
    ],
    enabled: true,
  },
  supply: {
    id: 'supply',
    slug: 'supply',
    name: 'Supply',
    description: 'Gestão de cadeia de suprimentos, estoque e logística',
    category: 'supply',
    icon: 'Truck',
    basePath: '/supply',
    dashboardPath: '/supply',
    route: '/supply',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/supply' },
    ],
    enabled: true,
  },
  production: {
    id: 'production',
    slug: 'production',
    name: 'Production',
    description: 'Planejamento e execução de produção, MRP e ordens',
    category: 'production',
    icon: 'Factory',
    basePath: '/production',
    dashboardPath: '/production',
    route: '/production',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/production' },
    ],
    enabled: true,
  },
  quality: {
    id: 'quality',
    slug: 'quality',
    name: 'Quality',
    description: 'Gestão de qualidade, auditorias e melhoria contínua',
    category: 'quality',
    icon: 'CheckCircle',
    basePath: '/quality',
    dashboardPath: '/quality',
    route: '/quality',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/quality' },
    ],
    enabled: true,
  },
  enterprise: {
    id: 'enterprise',
    slug: 'enterprise',
    name: 'Enterprise',
    description: 'Governança, compliance, risco e jurídico corporativo',
    category: 'enterprise',
    icon: 'Shield',
    basePath: '/enterprise',
    dashboardPath: '/enterprise',
    route: '/enterprise',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/enterprise' },
    ],
    enabled: true,
  },
  technology: {
    id: 'technology',
    slug: 'technology',
    name: 'Technology',
    description: 'Gestão de TI, infraestrutura e serviços de TI',
    category: 'technology',
    icon: 'Server',
    basePath: '/technology',
    dashboardPath: '/technology',
    route: '/technology',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/technology' },
    ],
    enabled: true,
  },
  cybersecurity: {
    id: 'cybersecurity',
    slug: 'cybersecurity',
    name: 'Cybersecurity',
    description: 'Operações de segurança, incidentes e gestão de vulnerabilidades',
    category: 'cybersecurity',
    icon: 'ShieldCheck',
    basePath: '/cybersecurity',
    dashboardPath: '/cybersecurity',
    route: '/cybersecurity',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/cybersecurity' },
    ],
    enabled: true,
  },
  data: {
    id: 'data',
    slug: 'data',
    name: 'Data',
    description: 'Inteligência de negócios, analytics e governança de dados',
    category: 'data',
    icon: 'Database',
    basePath: '/data',
    dashboardPath: '/data',
    route: '/data',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/data' },
    ],
    enabled: true,
  },
  knowledge: {
    id: 'knowledge',
    slug: 'knowledge',
    name: 'Knowledge',
    description: 'Documentação, wiki e gestão do conhecimento',
    category: 'knowledge',
    icon: 'BookOpen',
    basePath: '/knowledge',
    dashboardPath: '/knowledge',
    route: '/knowledge',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/knowledge' },
    ],
    enabled: true,
  },
  product: {
    id: 'product',
    slug: 'product',
    name: 'Product',
    description: 'Gestão de produto, roadmap e ciclo de vida',
    category: 'product',
    icon: 'Package',
    basePath: '/product',
    dashboardPath: '/product',
    route: '/product',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/product' },
    ],
    enabled: true,
  },
  innovation: {
    id: 'innovation',
    slug: 'innovation',
    name: 'Innovation',
    description: 'Pesquisa e desenvolvimento, ideias e experimentos',
    category: 'innovation',
    icon: 'FlaskConical',
    basePath: '/innovation',
    dashboardPath: '/innovation',
    route: '/innovation',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/innovation' },
    ],
    enabled: true,
  },
  ai: {
    id: 'ai',
    slug: 'ai',
    name: 'AI',
    description: 'Inteligência artificial, agentes e assistentes de IA',
    category: 'ai',
    icon: 'Brain',
    basePath: '/ai',
    dashboardPath: '/ai',
    route: '/ai',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/ai' },
    ],
    enabled: true,
  },
  automation: {
    id: 'automation',
    slug: 'automation',
    name: 'Automation',
    description: 'Automação de processos, workflows e integrações',
    category: 'automation',
    icon: 'Workflow',
    basePath: '/automation',
    dashboardPath: '/automation',
    route: '/automation',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/automation' },
    ],
    enabled: true,
  },
  facilities: {
    id: 'facilities',
    slug: 'facilities',
    name: 'Facilities',
    description: 'Gestão de instalações, prédios e reservas de espaço',
    category: 'facilities',
    icon: 'Building',
    basePath: '/facilities',
    dashboardPath: '/facilities',
    route: '/facilities',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/facilities' },
    ],
    enabled: true,
  },
  assets: {
    id: 'assets',
    slug: 'assets',
    name: 'Assets',
    description: 'Gestão de ativos, equipamentos e depreciação',
    category: 'assets',
    icon: 'Tag',
    basePath: '/assets',
    dashboardPath: '/assets',
    route: '/assets',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/assets' },
    ],
    enabled: true,
  },
  communication: {
    id: 'communication',
    slug: 'communication',
    name: 'Communication',
    description: 'Comunicação interna, anúncios e canais corporativos',
    category: 'communication',
    icon: 'MessageCircle',
    basePath: '/communication',
    dashboardPath: '/communication',
    route: '/communication',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/communication' },
    ],
    enabled: true,
  },
  'health-safety': {
    id: 'health-safety',
    slug: 'health-safety',
    name: 'Health & Safety',
    description: 'Saúde ocupacional, segurança do trabalho e PPE',
    category: 'health-safety',
    icon: 'HeartPulse',
    basePath: '/health-safety',
    dashboardPath: '/health-safety',
    route: '/health-safety',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/health-safety' },
    ],
    enabled: true,
  },
  international: {
    id: 'international',
    slug: 'international',
    name: 'International',
    description: 'Operações multi-país, multi-moeda e compliance internacional',
    category: 'international',
    icon: 'Globe',
    basePath: '/international',
    dashboardPath: '/international',
    route: '/international',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/international' },
    ],
    enabled: true,
  },
  franchise: {
    id: 'franchise',
    slug: 'franchise',
    name: 'Franchise',
    description: 'Gestão de franquias, royalties e desempenho de unidades',
    category: 'franchise',
    icon: 'Store',
    basePath: '/franchise',
    dashboardPath: '/franchise',
    route: '/franchise',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/franchise' },
    ],
    enabled: true,
  },
  ecommerce: {
    id: 'ecommerce',
    slug: 'ecommerce',
    name: 'E-commerce',
    description: 'Loja online, catálogo, pedidos e pagamentos',
    category: 'ecommerce',
    icon: 'ShoppingBasket',
    basePath: '/ecommerce',
    dashboardPath: '/ecommerce',
    route: '/ecommerce',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/ecommerce' },
    ],
    enabled: true,
  },
  ecosystem: {
    id: 'ecosystem',
    slug: 'ecosystem',
    name: 'Ecosystem',
    description: 'Gestão de parceiros, revendedores e afiliados',
    category: 'ecosystem',
    icon: 'Share2',
    basePath: '/ecosystem',
    dashboardPath: '/ecosystem',
    route: '/ecosystem',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/ecosystem' },
    ],
    enabled: true,
  },
  research: {
    id: 'research',
    slug: 'research',
    name: 'Research',
    description: 'Pesquisa de mercado, insights e inteligência competitiva',
    category: 'research',
    icon: 'Search',
    basePath: '/research',
    dashboardPath: '/research',
    route: '/research',
    status: 'coming_soon',
    version: '1.0.0',
    navigation: [
      { id: 'dashboard', label: 'Dashboard', path: '/research' },
    ],
    enabled: true,
  },
};

export function getAppManifest(appId: CyzorAppId): CyzorAppManifest | undefined {
  return APP_REGISTRY[appId];
}

export function getAllApps(): CyzorAppManifest[] {
  return Object.values(APP_REGISTRY).filter((app) => app.enabled);
}

export function getEnabledApps(): CyzorAppManifest[] {
  return Object.values(APP_REGISTRY).filter((app) => app.enabled && app.status !== 'disabled');
}

export function getAppsByCategory(category: AppCategory): CyzorAppManifest[] {
  return Object.values(APP_REGISTRY).filter((app) => app.enabled && app.category === category && app.status !== 'disabled');
}

export function resolveAppFromPath(pathname: string): CyzorAppId | undefined {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  for (const app of getAllApps()) {
    if (normalized === app.basePath || normalized.startsWith(app.basePath + '/')) {
      return app.id;
    }
  }
  return undefined;
}

export function getAvailableAppIds(): CyzorAppId[] {
  return Object.entries(APP_REGISTRY)
    .filter(([, app]) => app.enabled && app.status !== 'disabled')
    .map(([id]) => id as CyzorAppId);
}